// src/pages/onboarding-control/components/StepConfigCard.jsx

import React, { useState } from "react";
import { motion } from "framer-motion";

const StepConfigCard = ({
  step,
  index,
  totalSteps,
  onToggleEnabled,
  onToggleRequired,
  onMove,
  onUpdate,
  onDelete,
  packageTiers = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: step.name || "",
    title: step.title || step.name || "",
    description: step.description || "",
    icon: step.icon || "📌",
    color: step.color || "#4f46e5",
    tiers: step.tiers || [],
  });

  const handleSaveEdit = () => {
    onUpdate(step.id, editData);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditData({
      name: step.name || "",
      title: step.title || step.name || "",
      description: step.description || "",
      icon: step.icon || "📌",
      color: step.color || "#4f46e5",
      tiers: step.tiers || [],
    });
    setIsEditing(false);
  };

  const toggleTier = (tierId) => {
    const currentTiers = editData.tiers || [];
    if (currentTiers.includes(tierId)) {
      setEditData({
        ...editData,
        tiers: currentTiers.filter((t) => t !== tierId),
      });
    } else {
      setEditData({ ...editData, tiers: [...currentTiers, tierId] });
    }
  };

  // Get tier label
  const getTierLabel = (tierId) => {
    const tier = packageTiers.find((t) => t.id === tierId);
    return tier ? tier.label : tierId;
  };

  const getTierColor = (tierId) => {
    const tier = packageTiers.find((t) => t.id === tierId);
    return tier ? tier.color : "#6b7280";
  };

  return (
    <motion.div
      className={`step-card ${!step.enabled ? "disabled" : ""}`}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
    >
      {/* Order */}
      <div className="step-col step-col-order">
        <span className="step-number">{step.order || index + 1}</span>
      </div>

      {/* Status */}
      <div className="step-col step-col-status">
        <label className="toggle">
          <input
            type="checkbox"
            checked={step.enabled}
            onChange={() => onToggleEnabled(step.id)}
            disabled={step.id === "domain"}
            title={
              step.id === "domain"
                ? "The domain step is required and cannot be disabled."
                : undefined
            }
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      {/* Step Info */}
      <div className="step-col step-col-step">
        {isEditing ? (
          <div className="edit-form-inline">
            <div className="edit-row">
              <input
                type="text"
                value={editData.icon}
                onChange={(e) =>
                  setEditData({ ...editData, icon: e.target.value })
                }
                className="edit-icon-input"
                placeholder="Icon"
              />
              <input
                type="text"
                value={editData.name}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
                className="edit-name-input"
                placeholder="Step Dot Tag"
              />
              <input
                type="color"
                value={editData.color}
                onChange={(e) =>
                  setEditData({ ...editData, color: e.target.value })
                }
                className="edit-color-input"
              />
            </div>
            <input
              type="text"
              value={editData.title}
              onChange={(e) =>
                setEditData({ ...editData, title: e.target.value })
              }
              className="edit-desc-input"
              placeholder="Step Title Header"
            />
            <input
              type="text"
              value={editData.description}
              onChange={(e) =>
                setEditData({ ...editData, description: e.target.value })
              }
              className="edit-desc-input"
              placeholder="Step Description Subtitle"
            />
          </div>
        ) : (
          <div className="step-info">
            <span
              className="step-icon"
              style={{ background: step.color || "#4f46e5" }}
            >
              {step.icon || "📌"}
            </span>
            <div className="step-details">
              <span className="step-name">
                <strong>{step.name || "Untitled"}</strong>
                {step.title && step.title !== step.name && ` (${step.title})`}
              </span>
              <span className="step-description">{step.description || ""}</span>
            </div>
          </div>
        )}
      </div>

      {/* Tiers */}
      <div className="step-col step-col-tiers">
        {isEditing ? (
          <div className="tier-selector">
            {packageTiers.map((tier) => (
              <label key={tier.id} className="tier-checkbox">
                <input
                  type="checkbox"
                  checked={(editData.tiers || []).includes(tier.id)}
                  onChange={() => toggleTier(tier.id)}
                />
                <span>{tier.label}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="tier-badges">
            {(step.tiers || []).map((tierId) => (
              <span
                key={tierId}
                className="tier-badge"
                style={{
                  backgroundColor: getTierColor(tierId) + "20",
                  color: getTierColor(tierId),
                  borderColor: getTierColor(tierId),
                }}
              >
                {getTierLabel(tierId)}
              </span>
            ))}
            {(!step.tiers || step.tiers.length === 0) && (
              <span className="tier-badge all-tiers">All</span>
            )}
          </div>
        )}
      </div>

      {/* Required */}
      <div className="step-col step-col-required">
        <label className="toggle toggle-small">
          <input
            type="checkbox"
            checked={step.required}
            onChange={() => onToggleRequired(step.id)}
            disabled={!step.enabled || step.id === "domain"}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      {/* Actions */}
      <div className="step-col step-col-actions">
        {isEditing ? (
          <>
            <button className="action-btn save" onClick={handleSaveEdit}>
              ✓
            </button>
            <button className="action-btn cancel" onClick={handleCancelEdit}>
              ✕
            </button>
          </>
        ) : (
          <>
            <button
              className="action-btn move"
              onClick={() => onMove(step.id, "up")}
              disabled={index === 0}
            >
              ▲
            </button>
            <button
              className="action-btn move"
              onClick={() => onMove(step.id, "down")}
              disabled={index === totalSteps - 1}
            >
              ▼
            </button>
            <button
              className="action-btn edit"
              onClick={() => setIsEditing(true)}
            >
              ✎
            </button>
            <button
              className="action-btn delete"
              onClick={() => onDelete(step.id)}
              disabled={step.id === "domain"}
              title={
                step.id === "domain"
                  ? "The domain step is required and cannot be removed."
                  : undefined
              }
            >
              ✕
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default StepConfigCard;
